import {
  buildOrgxApiUrl,
  normalizeOrgxBaseUrl,
  resolveOrgxAuth,
  type ResolveOrgxAuthOptions,
} from "./auth.js";
import { isRecord } from "./fs.js";
import type { OrgxWorkspace } from "./workspaces.js";
import {
  readWizardState,
  updateWizardState,
  type WizardFirstValueInitiativeRecord,
  type WizardOnboardingTaskRecord,
} from "./wizard-state.js";

export const ONBOARDING_TASK_TITLE = "Complete OrgX onboarding";
export const ONBOARDING_TASK_SUMMARY =
  "Starter onboarding task created by @useorgx/wizard so the first workspace has a clear follow-up after setup.";
const ONBOARDING_WORKSTREAM_TITLE = "OrgX onboarding";
const ONBOARDING_WORKSTREAM_SUMMARY =
  "Starter onboarding workstream created by @useorgx/wizard so the first workspace has a home for setup follow-up tasks.";
export const FIRST_VALUE_INITIATIVE_TITLE = "Make OrgX useful on this machine";
export const FIRST_VALUE_INITIATIVE_SUMMARY =
  "First initiative created by @useorgx/wizard so setup ends with a live workspace, an onboarding workstream, and a clean handoff into the user's configured AI tools.";

export interface OrgxInitiative {
  id: string;
  summary?: string;
  title: string;
}

export interface OrgxTask {
  id: string;
  status?: string;
  summary?: string;
  title: string;
}

interface OrgxWorkstream {
  id: string;
  title: string;
}

export interface FirstValueInitiativeResult {
  created: boolean;
  initiative: OrgxInitiative;
  liveUrl: string;
}

interface InitiativeClientOptions extends ResolveOrgxAuthOptions {
  statePath?: string;
}

function parseResponseBody(text: string): unknown {
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function formatHttpError(status: number, body: unknown): string {
  if (typeof body === "string" && body.trim().length > 0) {
    return `HTTP ${status}: ${body}`;
  }

  if (isRecord(body) && typeof body.error === "string" && body.error.trim().length > 0) {
    return `HTTP ${status}: ${body.error}`;
  }

  return `HTTP ${status}`;
}

function extractEntity(payload: unknown): Record<string, unknown> {
  const entity = isRecord(payload) && isRecord(payload.data) ? payload.data : payload;
  if (!isRecord(entity)) {
    throw new Error("OrgX returned an unexpected entity payload.");
  }

  return entity;
}

function parseInitiative(payload: unknown): OrgxInitiative {
  const entity = extractEntity(payload);

  const id = typeof entity.id === "string" ? entity.id.trim() : "";
  const title = typeof entity.title === "string"
    ? entity.title.trim()
    : typeof entity.name === "string"
      ? entity.name.trim()
      : "";

  if (!id || !title) {
    throw new Error("OrgX returned an incomplete initiative payload.");
  }

  return {
    id,
    title,
    ...(typeof entity.summary === "string" && entity.summary.trim().length > 0
      ? { summary: entity.summary.trim() }
      : {}),
  };
}

function parseTask(payload: unknown): OrgxTask {
  const entity = extractEntity(payload);
  const id = typeof entity.id === "string" ? entity.id.trim() : "";
  const title = typeof entity.title === "string"
    ? entity.title.trim()
    : typeof entity.name === "string"
      ? entity.name.trim()
      : "";

  if (!id || !title) {
    throw new Error("OrgX returned an incomplete task payload.");
  }

  return {
    id,
    title,
    ...(typeof entity.status === "string" && entity.status.trim().length > 0
      ? { status: entity.status.trim() }
      : {}),
    ...(typeof entity.summary === "string" && entity.summary.trim().length > 0
      ? { summary: entity.summary.trim() }
      : {}),
  };
}

function parseWorkstream(payload: unknown): OrgxWorkstream {
  const entity = extractEntity(payload);
  const id = typeof entity.id === "string" ? entity.id.trim() : "";
  const title = typeof entity.title === "string"
    ? entity.title.trim()
    : typeof entity.name === "string"
      ? entity.name.trim()
      : "";

  if (!id || !title) {
    throw new Error("OrgX returned an incomplete workstream payload.");
  }

  return { id, title };
}

function toOnboardingTaskRecord(
  task: OrgxTask,
  workspace: Pick<OrgxWorkspace, "id" | "name">,
  options: {
    initiativeId?: string;
    workstreamId?: string;
  } = {},
): WizardOnboardingTaskRecord {
  return {
    createdAt: new Date().toISOString(),
    id: task.id,
    ...(options.initiativeId ? { initiativeId: options.initiativeId } : {}),
    ...(task.status ? { status: task.status } : {}),
    title: task.title,
    ...(options.workstreamId ? { workstreamId: options.workstreamId } : {}),
    workspaceId: workspace.id,
    workspaceName: workspace.name,
  };
}

function toFirstValueInitiativeRecord(
  initiative: OrgxInitiative,
  liveUrl: string,
  workspace: Pick<OrgxWorkspace, "id" | "name">,
): WizardFirstValueInitiativeRecord {
  return {
    createdAt: new Date().toISOString(),
    id: initiative.id,
    liveUrl,
    ...(initiative.summary ? { summary: initiative.summary } : {}),
    title: initiative.title,
    workspaceId: workspace.id,
    workspaceName: workspace.name,
  };
}

async function requireOrgxAuth(options: InitiativeClientOptions = {}) {
  const auth = await resolveOrgxAuth(options);
  if (!auth) {
    throw new Error(
      "No OrgX API key configured. Run `wizard auth login` or `wizard auth set-key <oxk_...>` first.",
    );
  }

  return auth;
}

async function createEntity<T>(
  type: string,
  body: Record<string, unknown>,
  parse: (payload: unknown) => T,
  options: InitiativeClientOptions = {},
): Promise<T> {
  const auth = await requireOrgxAuth(options);
  const response = await fetch(buildOrgxApiUrl("/entities", auth.baseUrl), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${auth.apiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      type,
      ...body,
    }),
    signal: AbortSignal.timeout(7_000),
  });

  const responseBody = parseResponseBody(await response.text());
  if (!response.ok) {
    throw new Error(`Failed to create ${type}. ${formatHttpError(response.status, responseBody)}`);
  }

  return parse(responseBody);
}

async function updateEntity<T>(
  type: string,
  id: string,
  body: Record<string, unknown>,
  parse: (payload: unknown) => T,
  options: InitiativeClientOptions = {},
): Promise<T> {
  const auth = await requireOrgxAuth(options);
  const response = await fetch(buildOrgxApiUrl("/entities", auth.baseUrl), {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${auth.apiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      type,
      id,
      ...body,
    }),
    signal: AbortSignal.timeout(7_000),
  });

  const responseBody = parseResponseBody(await response.text());
  if (!response.ok) {
    throw new Error(`Failed to update ${type}. ${formatHttpError(response.status, responseBody)}`);
  }

  return parse(responseBody);
}

function buildLiveUrl(baseUrl: string, initiativeId: string): string {
  const parsed = new URL(normalizeOrgxBaseUrl(baseUrl));
  parsed.pathname = `/live/${initiativeId}`;
  parsed.search = "";
  parsed.hash = "";
  return parsed.toString();
}

export async function createInitiative(
  input: {
    summary?: string;
    title: string;
    workspaceId?: string;
  },
  options: InitiativeClientOptions = {},
): Promise<OrgxInitiative> {
  const title = input.title.trim();
  if (!title) {
    throw new Error("Initiative title is required.");
  }

  return createEntity(
    "initiative",
    {
      title,
      status: "active",
      ...(input.summary?.trim() ? { summary: input.summary.trim() } : {}),
      ...(input.workspaceId?.trim() ? { workspace_id: input.workspaceId.trim() } : {}),
    },
    parseInitiative,
    options,
  );
}

export async function ensureFirstValueInitiative(
  workspace: Pick<OrgxWorkspace, "id" | "name">,
  options: InitiativeClientOptions & {
    summary?: string;
    title?: string;
  } = {},
): Promise<FirstValueInitiativeResult> {
  const existingRecord = readWizardState(options.statePath)?.firstValueInitiative;
  const matchingRecord = existingRecord?.workspaceId === workspace.id ? existingRecord : undefined;
  const auth = await requireOrgxAuth(options);

  if (matchingRecord) {
    return {
      created: false,
      initiative: {
        id: matchingRecord.id,
        title: matchingRecord.title,
        ...(matchingRecord.summary ? { summary: matchingRecord.summary } : {}),
      },
      liveUrl: matchingRecord.liveUrl || buildLiveUrl(auth.baseUrl, matchingRecord.id),
    };
  }

  const initiative = await createInitiative(
    {
      title: options.title?.trim() || FIRST_VALUE_INITIATIVE_TITLE,
      summary: options.summary?.trim() || FIRST_VALUE_INITIATIVE_SUMMARY,
      workspaceId: workspace.id,
    },
    options,
  );
  const liveUrl = buildLiveUrl(auth.baseUrl, initiative.id);

  updateWizardState(
    (current) => ({
      ...current,
      firstValueInitiative: toFirstValueInitiativeRecord(initiative, liveUrl, workspace),
    }),
    options.statePath,
  );

  return {
    created: true,
    initiative,
    liveUrl,
  };
}

export async function ensureOnboardingTask(
  workspace: Pick<OrgxWorkspace, "id" | "name">,
  options: InitiativeClientOptions & { initiativeId?: string } = {},
): Promise<OrgxTask> {
  const existingRecord = readWizardState(options.statePath)?.onboardingTask;
  if (existingRecord?.workspaceId === workspace.id) {
    return {
      id: existingRecord.id,
      title: existingRecord.title,
      ...(existingRecord.status ? { status: existingRecord.status } : {}),
    };
  }

  const initiativeId = options.initiativeId?.trim();
  if (!initiativeId) {
    throw new Error(
      "Starter onboarding task requires an initiative context. Re-run setup with the founder preset or create an initiative before requesting onboarding tasks.",
    );
  }

  const workstream = await createEntity(
    "workstream",
    {
      initiative_id: initiativeId,
      status: "active",
      summary: ONBOARDING_WORKSTREAM_SUMMARY,
      title: ONBOARDING_WORKSTREAM_TITLE,
      workspace_id: workspace.id,
    },
    parseWorkstream,
    options,
  );

  const task = await createEntity(
    "task",
    {
      initiative_id: initiativeId,
      status: "todo",
      summary: ONBOARDING_TASK_SUMMARY,
      title: ONBOARDING_TASK_TITLE,
      workstream_id: workstream.id,
      workspace_id: workspace.id,
    },
    parseTask,
    options,
  );

  updateWizardState(
    (current) => ({
      ...current,
      onboardingTask: toOnboardingTaskRecord(task, workspace, {
        initiativeId,
        workstreamId: workstream.id,
      }),
    }),
    options.statePath,
  );

  return task;
}
